/** Order-entry state, validation and submit, shared by the desktop order panel and the mobile order ticket. */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  clampLeverage,
  D,
  fmtPrice,
  fmtUsd,
  hedgedMargins,
  initialMargin,
  leverageCap,
  lotsToQty,
  maxNotional,
  notionalOf,
  openFeeFor,
  openFeeLabel,
  orderTypeLabel,
  pairLabel,
  pendingKind,
  qtyFromNotionalInLots,
  qtyToLots,
  slPctFromPrice,
  slPriceFromPct,
  tpPctFromPrice,
  tpPriceFromPct,
  tradeIdSuffix,
  usdPerPoint,
  validateTpSl,
  type Order,
  type PendingType,
  type Side,
} from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { nudgePush } from '../lib/push';
import { useInstrument, useQuote, useTicker } from '../stores/market';
import { useMe } from '../stores/session';
import { useLiveAccount, usePositions } from '../stores/trading';
import { sideLabel } from './ui';

export const DEC_RE = /^\d*\.?\d*$/;
const valid = (s: string) => s !== '' && s !== '.' && DEC_RE.test(s);
const dec = (s: string): D | null => (valid(s) ? D.from(s) : null);

export type TpSlInput = { kind: 'price' | 'pct' | 'usd'; value: string } | null;
export type SizeUnit = 'usd' | 'lots';
/** Market, or a pending order whose type (limit or stop) follows from its price. */
export type TicketType = 'market' | 'pending';

/** Why the order cannot be sent: shown under the blank button, with the field at fault outlined. */
export interface OrderProblem {
  field?: 'size' | 'price' | 'tp' | 'sl';
  title: string;
  text?: string;
  link?: { label: string; to: string };
}

const UNIT_KEY = 'orderSizeUnit';
function loadUnit(): SizeUnit {
  try {
    return localStorage.getItem(UNIT_KEY) === 'usd' ? 'usd' : 'lots';
  } catch {
    return 'lots';
  }
}

export function useOrderForm(symbol: string, initialSide?: Side) {
  const inst = useInstrument(symbol);
  const ticker = useTicker(symbol);
  const quote = useQuote(symbol);
  const me = useMe();
  const account = useLiveAccount();
  const positions = usePositions();

  const [side, setSide] = useState<Side>(initialSide ?? 'long');
  const [type, setType] = useState<TicketType>('market');
  const [lev, setLev] = useState(20);
  const [amount, setAmount] = useState('');
  const [unit, setUnitState] = useState<SizeUnit>(loadUnit);
  const [lots, setLots] = useState('');
  const [limit, setLimit] = useState('');
  const [tpsl, setTpsl] = useState(false);
  const [tp, setTp] = useState<TpSlInput>(null);
  const [sl, setSl] = useState<TpSlInput>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (initialSide) setSide(initialSide);
  }, [initialSide]);
  useEffect(() => {
    setLimit('');
    setTp(null);
    setSl(null);
    setMsg(null);
  }, [symbol]);
  // The instrument's maximum, or the client's own cap (set by staff) when lower.
  const maxLev = inst ? leverageCap(inst.maxLeverage, me?.maxLeverage) : 1;
  // Each instrument opens at its default leverage; keep the choice inside its min…max range
  // (max = the client's cap when that is lower).
  const levFor = useRef<string | null>(null);
  useEffect(() => {
    if (!inst) return;
    const range = { minLeverage: inst.minLeverage, maxLeverage: maxLev };
    if (levFor.current !== inst.symbol) {
      levFor.current = inst.symbol;
      setLev(clampLeverage(inst.defaultLeverage, range));
    } else if (lev !== clampLeverage(lev, range)) setLev(clampLeverage(lev, range));
  }, [inst, lev, maxLev]);
  // The volume opens at the instrument's minimum lots, and returns there on a symbol change.
  const lotsFor = useRef<string | null>(null);
  useEffect(() => {
    if (!inst || lotsFor.current === inst.symbol) return;
    lotsFor.current = inst.symbol;
    setLots(D.from(inst.minLots).toFixed(Math.max(0, (inst.lotStep.split('.')[1] ?? '').length)));
  }, [inst]);

  const dp = inst?.priceDecimals ?? 2;

  const ccy = inst?.priceCcy ?? 'USD';
  const feeRate = inst?.feeRate ?? '0';
  const feeFlat = inst?.feeFlat ?? '0';
  // "0.1%", "$1", "0.1% + $1" or "" (free), for the fee row.
  const feeLabel = openFeeLabel(feeRate, feeFlat);
  // USD per point per unit: contract size × the price currency's USD rate (USD/JPY etc.).
  const cs = usdPerPoint(inst?.contractSize ?? '1', quote?.fx);
  const lotSize = inst?.lotSize ?? '1';
  const lotStep = inst?.lotStep ?? '0.01';
  const minLots = inst?.minLots ?? '0.01';
  const lotDp = Math.max(0, (lotStep.split('.')[1] ?? '').length);
  const qdp = inst?.qtyDecimals ?? 4;
  const byLots = unit === 'lots';
  const available = account?.available ?? '0';
  const funded = !!account && D.from(account.balance).add(account.credit).isPos();

  const ref: D | null = useMemo(() => {
    if (type === 'pending') return dec(limit);
    if (!quote) return null;
    return D.from(side === 'long' ? quote.ask : quote.bid);
  }, [type, limit, quote, side]);
  // Open-side price (ask for a buy, bid for a sell) that a pending price is measured against.
  const openRef = quote ? D.from(side === 'long' ? quote.ask : quote.bid) : null;
  // limit / stop from where the price sits; 'same' when it equals the open-side price.
  const pendKind: PendingType | 'same' | null =
    type === 'pending' && quote && ref && ref.isPos() ? (pendingKind(side, { bid: D.from(quote.bid), ask: D.from(quote.ask) }, ref) ?? 'same') : null;
  const pendType: PendingType | null = pendKind === 'same' ? null : pendKind;
  const orderLabel = type === 'market' ? sideLabel(side) : pendType ? orderTypeLabel(side, pendType) : `${sideLabel(side)} Limit / Stop`;

  const maxN = useMemo(() => maxNotional(available, lev, feeRate, feeFlat), [available, lev, feeRate, feeFlat]);
  const lotsD = dec(lots) ?? D.ZERO;
  const lotsQty = lotsToQty(lotsD, lotSize);
  // Order value in USD: typed directly, or lots × lot size × price.
  const amt = byLots ? (ref && ref.isPos() ? notionalOf(lotsQty, ref, cs) : D.ZERO) : (dec(amount) ?? D.ZERO);
  const hasSize = byLots ? lotsD.isPos() : amt.isPos();
  const pct = maxN.isPos() ? Math.min(100, Math.max(0, amt.div(maxN).mul(100).toNumber())) : 0;
  const unitValue = ref && ref.isPos() ? ref.mul(lotSize).mul(cs) : null;
  const maxLots = unitValue ? maxN.div(unitValue, 'down').div(lotStep, 'down').round(0, 'down').mul(lotStep) : D.ZERO;

  const hedgeRate = inst?.hedgedMarginRate ?? '0';
  const calc = useMemo(() => {
    if (!ref || !ref.isPos() || !amt.isPos()) return null;
    // An amount in USD opens whole lot steps (rounded down), as the server fills it.
    const qty = byLots ? lotsQty : qtyFromNotionalInLots(amt, ref, cs, lotSize, lotStep);
    const value = byLots ? amt : notionalOf(qty, ref, cs);
    const fullMargin = initialMargin(value, lev);
    const fee = openFeeFor(value, feeRate, feeFlat);
    // A market order against open positions on the other side only adds the margin of its uncovered
    // part (hedged volume is free). A pending order reserves full margin until it fills.
    const group = positions.filter((p) => p.symbol === symbol);
    const hedges = type === 'market' && group.some((p) => p.side !== side);
    const before = group.reduce((s, p) => s.add(p.marginHeld ?? p.margin), D.ZERO);
    const after = hedgedMargins([...group, { side, qty, margin: fullMargin }], hedgeRate).reduce((s, m) => s.add(m), D.ZERO);
    const margin = hedges ? D.max(D.ZERO, after.sub(before)) : fullMargin;
    return { qty, value, lots: qtyToLots(qty, lotSize), margin, fee, hedges, pointValue: qty.mul(cs) };
  }, [ref, amt.raw, cs, lev, feeRate, feeFlat, side, dp, byLots, lotsQty.raw, lotSize, lotStep, positions, symbol, type, hedgeRate]); // eslint-disable-line react-hooks/exhaustive-deps

  const setUnit = (u: SizeUnit) => {
    setUnitState(u);
    try {
      localStorage.setItem(UNIT_KEY, u);
    } catch {
      /* storage unavailable */
    }
  };
  const stepLots = (dir: 1 | -1) => {
    const next = lotsD.add(D.from(lotStep).mul(dir));
    setLots((next.lt(minLots) ? D.from(minLots) : next).toFixed(lotDp));
  };

  // TP/SL: whichever field the user last edited is the source of truth; the others follow the reference price and size.
  // `usd` is the estimated profit (TP) or loss (SL) at that price; `dir` is +1 for TP, -1 for SL.
  const pv = calc?.pointValue ?? null;
  const tpslVals = (input: TpSlInput, dir: 1 | -1) => {
    const empty = { price: '', pct: '', usd: '', d: null as D | null };
    if (!input) return empty;
    const raw = { ...empty, [input.kind]: input.value };
    if (!ref || !valid(input.value)) return raw;
    const gain = (side === 'long' ? 1 : -1) * dir; // price direction that reaches this level
    let p: D;
    if (input.kind === 'price') p = D.from(input.value);
    else if (input.kind === 'pct') p = (dir === 1 ? tpPriceFromPct : slPriceFromPct)(side, ref, input.value).round(dp);
    else {
      if (!pv || !pv.isPos()) return raw;
      const move = D.from(input.value).div(pv);
      p = (gain === 1 ? ref.add(move) : ref.sub(move)).round(dp);
    }
    const pct = (dir === 1 ? tpPctFromPrice : slPctFromPrice)(side, ref, p).toFixed(2);
    const diff = gain === 1 ? p.sub(ref) : ref.sub(p);
    const usd = pv ? diff.mul(pv).toFixed(2) : '';
    return {
      price: input.kind === 'price' ? input.value : p.toFixed(dp),
      pct: input.kind === 'pct' ? input.value : pct,
      usd: input.kind === 'usd' ? input.value : usd,
      d: p,
    };
  };
  const tpVals = useMemo(() => tpslVals(tp, 1), [tp, ref, side, dp, pv?.raw]); // eslint-disable-line react-hooks/exhaustive-deps
  const slVals = useMemo(() => tpslVals(sl, -1), [sl, ref, side, dp, pv?.raw]); // eslint-disable-line react-hooks/exhaustive-deps

  const px = (x: D) => fmtPrice(x, dp, inst?.priceCcy ?? 'USD');
  const problem = useMemo((): OrderProblem | null => {
    if (!inst) return { title: 'Loading instrument…' };
    if (!me) return { title: 'Log in to trade.', text: 'You need an account to place orders.', link: { label: 'Log in', to: '/login' } };
    if (ticker && !ticker.open) return { title: 'The market is closed.', text: 'Orders can be placed again when it reopens.' };
    if (ticker?.paused && account?.kind === 'live') return { title: 'Live price unavailable.', text: 'Trading is paused until the live price is back.' };
    if (!funded) {
      const need = calc ? ` Margin needed for this order: ${fmtUsd(calc.margin.add(calc.fee))}.` : '';
      return { title: 'Your account has no funds.', text: `Deposit to place this order.${need}`, link: { label: 'Deposit', to: '/account/deposit' } };
    }
    if (!hasSize) {
      return byLots
        ? { title: 'Enter a volume.', text: `The minimum is ${minLots} lots.` }
        : { title: 'Enter an amount.', text: `The minimum order is ${fmtUsd(inst.minNotional)}.` };
    }
    if (byLots) {
      if (lotsD.lt(minLots)) return { field: 'size', title: 'Volume too small.', text: `The minimum is ${minLots} lots.` };
      if (!lotsD.div(lotStep).round(0).mul(lotStep).eq(lotsD)) {
        const lo = lotsD.div(lotStep, 'down').round(0, 'down').mul(lotStep);
        return { field: 'size', title: `Volume must be in steps of ${lotStep} lots.`, text: `Try ${lo.toFixed(lotDp)} or ${lo.add(lotStep).toFixed(lotDp)}.` };
      }
    }
    if (type === 'pending') {
      const rn = side === 'long' ? 'Ask' : 'Bid';
      if (!ref || !ref.isPos()) {
        const at = openRef ? ` (${px(openRef)})` : '';
        return {
          title: 'Enter an order price.',
          text: side === 'long' ? `Below the Ask${at} makes a Buy Limit; above it makes a Buy Stop.` : `Above the Bid${at} makes a Sell Limit; below it makes a Sell Stop.`,
        };
      }
      if (pendKind === 'same') {
        return {
          field: 'price',
          title: `This price equals the current ${rn} (${px(openRef!)}).`,
          text: `A Limit or Stop order needs a price above or below it. To ${side === 'long' ? 'buy' : 'sell'} at the current price, use Market.`,
        };
      }
    }
    if (!byLots && calc && calc.lots.lt(minLots)) {
      const least = unitValue ? ` (about ${fmtUsd(unitValue.mul(minLots))} at this price)` : '';
      return { field: 'size', title: 'Order too small.', text: `This amount buys less than the minimum of ${minLots} lots${least}.` };
    }
    if (amt.lt(inst.minNotional)) return { field: 'size', title: 'Order too small.', text: `The minimum order value is ${fmtUsd(inst.minNotional)}; this one is ${fmtUsd(amt)}.` };
    if (amt.gt(inst.maxNotional)) return { field: 'size', title: 'Order too large.', text: `The maximum order value is ${fmtUsd(inst.maxNotional)}; this one is ${fmtUsd(amt)}.` };
    if (calc && calc.margin.add(calc.fee).gt(available)) {
      const fix = byLots && maxLots.gte(minLots) ? `Lower the volume to ${maxLots.toFixed(lotDp)} lots or less` : !byLots && maxN.isPos() ? `Lower the amount to ${fmtUsd(maxN.round(2, 'down'))} or less` : 'Deposit more';
      return {
        field: 'size',
        title: 'Not enough free margin.',
        text: `This order needs ${fmtUsd(calc.margin.add(calc.fee))} (margin + fee); you have ${fmtUsd(available)} available. ${fix}, raise leverage, or deposit.`,
        link: { label: 'Deposit', to: '/account/deposit' },
      };
    }
    if (tpsl && ref) {
      const up = side === 'long';
      const dir = up ? 'buy' : 'sell';
      if (tpVals.d && validateTpSl(side, ref, tpVals.d, null)) {
        return { field: 'tp', title: `Take profit must be ${up ? 'above' : 'below'} your entry (${px(ref)}) for a ${dir}.` };
      }
      if (slVals.d && validateTpSl(side, ref, null, slVals.d)) {
        return { field: 'sl', title: `Stop loss must be ${up ? 'below' : 'above'} your entry (${px(ref)}) for a ${dir}.` };
      }
    }
    return null;
  }, [inst, me, ticker, account?.kind, funded, hasSize, byLots, lotsD.raw, minLots, lotStep, lotDp, type, ref, pendKind, openRef?.raw, side, amt.raw, calc, available, maxLots.raw, maxN.raw, tpsl, tpVals.d, slVals.d, dp]); // eslint-disable-line react-hooks/exhaustive-deps

  const net = useMemo(() => {
    let q = D.ZERO;
    for (const p of positions) if (p.symbol === symbol) q = p.side === 'long' ? q.add(p.qty) : q.sub(p.qty);
    return q;
  }, [positions, symbol]);

  const setPct = (p: number) => {
    setAmount(p <= 0 ? '' : maxN.mul(p).div(100).round(2, 'down').toFixed(2));
  };


  // An amount in USD names the value of the whole lot steps it buys (what the server fills), which can be
  // less than the amount typed.
  const sizeTxt = !hasSize ? '' : byLots ? `${lotsD.toFixed(lotDp)} lots ` : `${fmtUsd(calc && calc.qty.isPos() ? calc.value : amt)} of `;
  /** The submit button: always the action, e.g. "Buy Limit 0.10 lots BTC/USD @ $64,500.00". */
  const ctaLabel = `${orderLabel} ${sizeTxt}${pairLabel(symbol)}${type === 'pending' && ref && ref.isPos() ? ` @ ${px(ref)}` : ''}`;

  /** Sends the order. Resolves to the success message, or null. */
  async function submit(): Promise<string | null> {
    if (!account || problem) return null;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post<{ order: Order }>(
        '/api/orders',
        {
          accountId: account.id,
          symbol,
          side,
          type: type === 'market' ? 'market' : pendType,
          ...(byLots ? { lots: lotsD.toString() } : { notional: amt.toString() }),
          leverage: lev,
          limitPrice: type === 'pending' ? ref?.toString() : undefined,
          tpPrice: tpsl && tpVals.d ? tpVals.d.toString() : undefined,
          slPrice: tpsl && slVals.d ? slVals.d.toString() : undefined,
        },
        { idempotencyKey: crypto.randomUUID() },
      );
      const text = (type === 'market' ? `${sideLabel(side)} ${sizeTxt}${pairLabel(symbol)} opened` : `${orderLabel} order placed`) + tradeIdSuffix(res.order.tradeId);
      setMsg({ ok: true, text });
      nudgePush('order');
      setAmount('');
      setLots(D.from(minLots).toFixed(lotDp));
      return text;
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Order failed' });
      return null;
    } finally {
      setBusy(false);
    }
  }

  return {
    inst,
    quote,
    me,
    account,
    dp,
    ccy: inst?.priceCcy ?? 'USD',
    feeRate,
    feeFlat,
    feeLabel,
    available,
    funded,
    side,
    setSide,
    type,
    setType,
    lev,
    setLev,
    maxLev,
    amount,
    setAmount,
    unit,
    setUnit,
    lots,
    setLots,
    limit,
    setLimit,
    tpsl,
    setTpsl,
    setTp,
    setSl,
    tpVals,
    slVals,
    ref,
    maxN,
    lotsD,
    lotsQty,
    amt,
    hasSize,
    pct,
    maxLots,
    calc,
    stepLots,
    problem,
    orderLabel,
    pendType,
    openRef,
    ctaLabel,
    net,
    setPct,
    busy,
    msg,
    submit,
    sizeTxt,
    lotSize,
    lotStep,
    minLots,
    lotDp,
    qdp,
    byLots,
  };
}
